/** Peças comuns das telas da Programação, no portal e no módulo da equipe. */
import { useEffect, useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { CircleSlash, type LucideIcon } from 'lucide-react'
import { StatusChip } from '../../../shared/components/StatusChip'
import { useI18n } from '@/core/i18n/useI18n'
import { Info } from '../../dp/comum/lista'
import type { StatusDaProgramacao } from './api'

const TOM_DO_STATUS = { previa: 'warning', confirmada: 'success', cancelada: 'neutral' } as const

/** Prévia em destaque, porque pede a confirmação do cliente. A cancelada fica apagada
 *  (Samuel, 04/11/2026): a linha continua na lista, sem pedir nada. */
export function ChipDoStatus({ status }: { status: StatusDaProgramacao }) {
  const { t } = useI18n()
  return <StatusChip label={t(`programacao.status.${status}`)} tone={TOM_DO_STATUS[status] ?? 'neutral'} />
}

/** A barra de ações fixa no pé da tela, no molde do formulário de usuário. */
export function RodapeFixo({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        position: 'sticky', bottom: 0, zIndex: (tema) => tema.zIndex.appBar - 1,
        mx: { xs: -2, md: -4 }, px: { xs: 2, md: 4 }, py: 1.5, mt: 1,
        display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
        bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider', backdropFilter: 'blur(6px)',
      }}
    >
      {children}
    </Box>
  )
}

export interface ItemDoCancelamento { icone: LucideIcon; texto: ReactNode; dica?: string }

/**
 * A confirmação do cancelamento, no portal e na equipe (Samuel, 04/11/2026). O que
 * sai vai em linhas com ícone; a equipe digita o motivo, como no ajuste. Diálogo de
 * confirmação, e não gaveta: é uma pergunta só, com a resposta na mesma tela.
 */
export function DialogoDeCancelamento({
  aberto, titulo, texto, itens, aviso, comMotivo = false, rotuloMotivo, dicaMotivo, confirmar, manter, enviando,
  rotuloEnviando, onConfirmar, onFechar,
}: {
  aberto: boolean
  titulo: string
  texto: string
  itens: ItemDoCancelamento[]
  aviso?: { icone: LucideIcon; texto: string; cor: string } | null
  comMotivo?: boolean
  rotuloMotivo?: string
  dicaMotivo?: string
  confirmar: string
  manter: string
  enviando: boolean
  rotuloEnviando: string
  onConfirmar: (motivo: string) => void
  onFechar: () => void
}) {
  const [motivo, setMotivo] = useState('')
  useEffect(() => { if (!aberto) setMotivo('') }, [aberto])
  const motivoOk = !comMotivo || motivo.trim().length >= 3
  return (
    <Dialog open={aberto} onClose={enviando ? undefined : onFechar} maxWidth="xs" fullWidth aria-labelledby="cancelar-programacao-titulo">
      <DialogTitle id="cancelar-programacao-titulo" sx={{ fontFamily: '"Fredoka", sans-serif', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ display: 'inline-flex', color: 'error.main' }}><CircleSlash size={20} /></Box>
        {titulo}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{texto}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {itens.map((item, i) => (
            <Info key={i} icone={item.icone} cor="text.primary" tamanho={13} dica={item.dica}>{item.texto}</Info>
          ))}
        </Box>
        {aviso && <Info icone={aviso.icone} cor={aviso.cor} tamanho={13} forte>{aviso.texto}</Info>}
        {comMotivo && (
          <TextField value={motivo} onChange={(e) => setMotivo(e.target.value)} label={rotuloMotivo} helperText={dicaMotivo}
            multiline minRows={2} required fullWidth disabled={enviando} error={motivo !== '' && !motivoOk}
            inputProps={{ maxLength: 2000 }} />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onFechar} disabled={enviando} color="inherit">{manter}</Button>
        <Button variant="contained" color="error" disabled={enviando || !motivoOk} onClick={() => onConfirmar(motivo.trim())}
          startIcon={<CircleSlash size={16} />}>
          {enviando ? rotuloEnviando : confirmar}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
